import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, useAuth } from "../App";
import { Plus, FileText, Search, RefreshCw, ArrowLeft, Clock, ChevronDown, ChevronRight, LayoutGrid, List, Flame, X, MoreHorizontal, Calendar, TrendingUp, User, Users, CheckCircle2, RotateCcw, SlidersHorizontal, Paperclip, Trash2 } from "lucide-react";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import UserAutocomplete from "../components/UserAutocomplete";
import Hint from "../components/Hint";
import FormCarousel from "../components/FormCarousel";
let ReactQuill;
try { ReactQuill = require("react-quill-new").default; } catch (e) { ReactQuill = null; }
import { toast } from "sonner";
import "react-quill-new/dist/quill.snow.css";
import { AnimatedPage, AnimatedCard, StaggerContainer, StaggerItem, SectionHeader } from "../BizDevAnimations";
import "../BizDevAnimations.css";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";
import usePullToRefresh from "../hooks/usePullToRefresh";
import useIsMobile from "../hooks/useIsMobile";
import useShortcuts from "../hooks/useShortcuts";
import useUrlState from "../hooks/useUrlState";

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */
const STATUS_COLORS = { New: "#64748B", Assigned: "#0EA5E9", "In Progress": "#3B82F6", Pitched: "#A855F7", Won: "#22C55E", Executed: "#059669", Lost: "#6B7280", "On Hold": "#94A3B8" };
const PRIORITY_COLORS = { Low: "#64748B", Medium: "#F59E0B", High: "#F97316", Critical: "#EF4444" };
const SERVICE_COLORS = { "Social & Digital Retainer": "#06B6D4", "Mainline Campaign": "#A855F7", "Mainline Ad Film Production": "#A855F7", "Digital Campaign": "#EC4899", "Digital Content Production": "#3B82F6", "Influencer Marketing": "#F59E0B", "Media Buying": "#22C55E" };
const FILTER_GROUPS = [
  { label: "All", statuses: [] },
  { label: "In Progress", statuses: ["New", "Assigned", "In Progress"] },
  { label: "Pitched", statuses: ["Pitched"] },
  { label: "Won", statuses: ["Won", "Executed"] },
  { label: "Lost", statuses: ["Lost", "On Hold"] },
];
const BRIEF_STATUSES = [
  { key: "New", label: "New", color: "#64748B" }, { key: "Assigned", label: "Assigned", color: "#0EA5E9" },
  { key: "In Progress", label: "In Progress", color: "#3B82F6" }, { key: "Pitched", label: "Pitched", color: "#A855F7" },
  { key: "Won", label: "Won", color: "#22C55E" }, { key: "Lost", label: "Lost", color: "#EF4444" },
  { key: "On Hold", label: "On Hold", color: "#F97316" },
];
const KANBAN_STATUSES_HIDE = ["Executed", "Lost"];

/* ═══════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════ */
function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return dateStr; }
}

function pitchCountdown(dateStr) {
  if (!dateStr) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const pitch = new Date(dateStr + "T00:00:00"); pitch.setHours(0, 0, 0, 0);
  const diff = Math.round((pitch - now) / 86400000);
  if (diff < -1) return { text: `${Math.abs(diff)}d overdue`, color: "#EF4444", bg: "bg-red-500/10", overdue: true, diff };
  if (diff === -1) return { text: "1d overdue", color: "#EF4444", bg: "bg-red-500/10", overdue: true, diff };
  if (diff === 0) return { text: "Today", color: "#F59E0B", bg: "bg-amber-500/10", overdue: false, diff };
  if (diff === 1) return { text: "Tomorrow", color: "#EAB308", bg: "bg-yellow-500/8", overdue: false, diff };
  if (diff <= 3) return { text: `${diff}d left`, color: "#F59E0B", bg: "bg-amber-500/8", overdue: false, diff };
  if (diff <= 7) return { text: `${diff}d left`, color: "#22C55E", bg: "bg-green-500/6", overdue: false, diff };
  return { text: `${diff}d`, color: "#666", bg: "", overdue: false, diff };
}

function fmtValue(v) {
  if (!v && v !== 0) return "";
  if (Math.abs(v) >= 100) return `₹${(v / 100).toFixed(1)}Cr`;
  return `₹${Number(v).toFixed(0)}L`;
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function CompanyLogo({ name, website, size = 20 }) {
  const [failed, setFailed] = useState(false);
  const domain = website && website !== "Unknown" ? website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : null;
  if (domain && !failed) {
    return <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} width={size} height={size} alt={name} style={{ borderRadius: size > 30 ? 10 : 6, objectFit: "contain", background: "var(--s3)", flexShrink: 0 }} onError={() => setFailed(true)} />;
  }
  const initials = (name || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: size > 30 ? 10 : 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, fontWeight: 700, background: "var(--s3)", color: "var(--t2)", flexShrink: 0 }}>{initials}</div>;
}

const AVATAR_COLORS = [
  { bg: "rgba(59,130,246,0.12)", color: "#60A5FA" },
  { bg: "rgba(227,25,55,0.12)", color: "#E31937" },
  { bg: "rgba(139,92,246,0.12)", color: "#A78BFA" },
  { bg: "rgba(245,158,11,0.12)", color: "#FBBF24" },
  { bg: "rgba(6,182,212,0.12)", color: "#06B6D4" },
  { bg: "rgba(34,197,94,0.12)", color: "#4ADE80" },
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/* ═══════════════════════════════════════
   INLINE STATUS DROPDOWN
   ═══════════════════════════════════════ */
function StatusDropdown({ briefId, currentStatus, onUpdate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const handleChange = async (e, newStatus) => {
    e.stopPropagation();
    setOpen(false);
    try {
      await api.put(`/bizdev/briefs/${briefId}`, { status: newStatus });
      onUpdate();
      toast.success(`Status → ${newStatus}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };
  const c = STATUS_COLORS[currentStatus] || "#64748B";
  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors cursor-pointer hover:brightness-125"
        style={{ background: c + "18", color: c }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
        {currentStatus}
        <ChevronDown size={10} className="opacity-40" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[130px]">
          {BRIEF_STATUSES.map(s => (
            <button key={s.key} onClick={(e) => handleChange(e, s.key)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--s2)] transition-colors flex items-center gap-2 ${s.key === currentStatus ? "font-medium" : ""}`}
              style={{ color: s.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   FOCUS BANNER
   ═══════════════════════════════════════ */
function FocusBanner({ briefs, onFilterOverdue }) {
  const [dismissed, setDismissed] = useState(false);
  const stats = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    let overdue = 0, pitchThisWeek = 0, overdueValue = 0;
    briefs.forEach(b => {
      if (["Won", "Executed", "Lost", "On Hold"].includes(b.status)) return;
      if (b.pitch_date) {
        const d = new Date(b.pitch_date + "T00:00:00"); d.setHours(0, 0, 0, 0);
        const diff = Math.round((d - now) / 86400000);
        if (diff < 0) { overdue++; overdueValue += (b.opportunity_value_lakhs || 0); }
        if (diff >= 0 && diff <= 7) pitchThisWeek++;
      }
    });
    return { overdue, pitchThisWeek, overdueValue };
  }, [briefs]);

  if (dismissed || (stats.overdue === 0 && stats.pitchThisWeek === 0)) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-purple-500/10 bg-gradient-to-r from-purple-500/[.04] to-amber-500/[.02] mb-4">
      <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
        <Calendar size={16} className="text-purple-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">Upcoming pitches</p>
        <p className="text-xs text-[#999]">
          {stats.pitchThisWeek > 0 && <><strong className="text-purple-400">{stats.pitchThisWeek} pitches this week</strong> · </>}
          {stats.overdue > 0 && <><strong className="text-red-400">{stats.overdue} overdue</strong>{stats.overdueValue > 0 && ` · ${fmtValue(stats.overdueValue)} at risk`}</>}
        </p>
      </div>
      {stats.overdue > 0 && (
        <button onClick={onFilterOverdue} className="px-3 py-1.5 text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors whitespace-nowrap">
          Show overdue
        </button>
      )}
      <button onClick={() => setDismissed(true)} className="text-[#666] hover:text-[var(--t1)] transition-colors p-1"><X size={14} /></button>
    </div>
  );
}


/* ═══════════════════════════════════════
   MY BRIEFS DASHBOARD (To Do / To Review / Delegated)
   ═══════════════════════════════════════ */
function MyBriefsDashboard({ briefs, navigate, onUpdate, mode = "todo", canViewFinancials, leads = [] }) {
  const active = briefs.filter(b => b.status !== "Won" && b.status !== "Executed" && b.status !== "Lost");
  const done = briefs.filter(b => b.status === "Won" || b.status === "Executed" || b.status === "Lost");

  return (
    <div className="space-y-2">
      {active.length === 0 && done.length === 0 && (
        <div className="text-center py-16 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl">
          <FileText size={32} className="mx-auto text-[#222] mb-3" />
          <p className="text-sm text-[#555]">
            {mode === "todo" ? "No briefs assigned to you" : mode === "review" ? "No briefs to review" : "No briefs delegated by you"}
          </p>
        </div>
      )}
      {active.length > 0 && (
        <div className="rounded-xl border border-[var(--bdr)] overflow-hidden">
          {active.map(b => (
            <MyBriefRow key={b.brief_id} b={b} navigate={navigate} onUpdate={onUpdate} mode={mode} canViewFinancials={canViewFinancials} leads={leads} />
          ))}
        </div>
      )}
      {done.length > 0 && (
        <details className="group">
          <summary className="flex items-center gap-2 px-2 py-2 cursor-pointer text-xs text-[#555] hover:text-[#888] transition-colors select-none">
            <ChevronRight size={12} className="group-open:rotate-90 transition-transform" />
            Completed ({done.length})
          </summary>
          <div className="rounded-xl border border-[var(--bdr)] overflow-hidden mt-1 opacity-60">
            {done.map(b => (
              <MyBriefRow key={b.brief_id} b={b} navigate={navigate} onUpdate={onUpdate} mode={mode} canViewFinancials={canViewFinancials} leads={leads} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function MyBriefRow({ b, navigate, onUpdate, mode = "todo", canViewFinancials, leads = [] }) {
  const pc = pitchCountdown(b.pitch_date);
  const svcColor = SERVICE_COLORS[b.service_type] || "#06B6D4";
  const statusColor = STATUS_COLORS[b.status] || "#64748B";
  const isDone = b.status === "Won" || b.status === "Executed" || b.status === "Lost";

  const handleApprove = async (e) => {
    e.stopPropagation();
    try {
      await api.put(`/bizdev/briefs/${b.brief_id}`, { status: "Pitched" });
      toast.success("Brief approved → Pitched");
      onUpdate();
    } catch { toast.error("Failed to approve"); }
  };

  const handleRevise = async (e) => {
    e.stopPropagation();
    try {
      await api.put(`/bizdev/briefs/${b.brief_id}`, { status: "In Progress" });
      toast.success("Sent back for revision");
      onUpdate();
    } catch { toast.error("Failed to revise"); }
  };

  // Who to show based on mode
  let contextName = null;
  if (mode === "delegated") contextName = b.assigned_to_name || b.sub_assigned_to_name;
  else if (mode === "review") contextName = b.assigned_to_name;
  else contextName = b.created_by_name;

  const avatarColor = getAvatarColor(contextName);

  return (
    <div onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
      className={`flex items-center gap-3 px-4 py-3 border-b border-[var(--bdr)] cursor-pointer transition-colors group ${isDone ? "opacity-60" : ""} ${pc?.overdue ? "bg-red-500/[.015] hover:bg-red-500/[.03]" : "hover:bg-white/[.02]"}`}>
      {/* Status dot */}
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusColor }} />
      {/* Title + tags */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium leading-tight truncate ${isDone ? "line-through text-[#555]" : "text-white/90"}`}>{b.title || "Untitled"}</p>
          {b.is_retainer && <span className="text-[8px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 flex-shrink-0">RET</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {(b.company_name || b.lead_name) && (
            <span className="flex items-center gap-1.5 text-xs text-purple-400/80">
              <CompanyLogo name={b.company_name || b.lead_name} website={leads.find(l => l.lead_id === b.lead_id)?.website} size={16} />
              {b.company_name || b.lead_name}
            </span>
          )}
          {b.service_type && <span className="text-xs" style={{ color: svcColor }}>{b.service_type.length > 24 ? b.service_type.slice(0, 22) + "…" : b.service_type}</span>}
        </div>
      </div>
      {/* Priority */}
      <span className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: PRIORITY_COLORS[b.priority] || "#666" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_COLORS[b.priority] || "#666" }} />
        {b.priority}
      </span>
      {/* Status */}
      <span className="text-xs px-2.5 py-1 rounded-md flex-shrink-0" style={{ background: statusColor + "18", color: statusColor }}>
        {b.status}
      </span>
      {/* Pitch date */}
      {pc ? (
        <span className="text-xs flex items-center gap-1 flex-shrink-0" style={{ color: pc.color }}>
          <Clock size={10} />{pc.text}
        </span>
      ) : (
        <span className="text-xs text-[#555] flex-shrink-0 w-[60px]">No date</span>
      )}
      {/* Value */}
      {canViewFinancials && (
        <span className="text-xs font-semibold text-green-400 font-heading flex-shrink-0 w-[50px] text-right">
          {b.opportunity_value_lakhs ? fmtValue(b.opportunity_value_lakhs) : "—"}
        </span>
      )}
      {/* Review actions */}
      {mode === "review" && !isDone && (
        <div className="flex gap-1.5 flex-shrink-0">
          <button onClick={handleApprove} title="Approve" className="px-2.5 py-1 text-xs rounded-md bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors flex items-center gap-1">
            <CheckCircle2 size={11} /> Approve
          </button>
          <button onClick={handleRevise} title="Revise" className="px-2.5 py-1 text-xs rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-colors flex items-center gap-1">
            <RotateCcw size={11} /> Revise
          </button>
        </div>
      )}
      {/* Context person */}
      {contextName && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {mode === "delegated" && <span className="text-xs text-[#555]">→</span>}
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold" style={{ background: avatarColor.bg, color: avatarColor.color }} title={contextName}>
            {getInitials(contextName)}
          </div>
          <span className="text-xs text-[#666] max-w-[80px] truncate hidden lg:inline">{contextName}</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   MOBILE BRIEF CARD
   ═══════════════════════════════════════ */
function MobileBriefCard({ b, navigate, leads, canViewFinancials, canDeleteBrief, onDeleteInline }) {
  const pc = pitchCountdown(b.pitch_date);
  const statusColor = STATUS_COLORS[b.status] || "#64748B";
  const lead = leads.find(l => l.lead_id === b.lead_id);
  const avatarColor = getAvatarColor(b.assigned_to_name);
  return (
    <div onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)} className="dm-mobile-brief-card"
      style={{ background: 'var(--s1)', borderRadius: 16, padding: 16, marginBottom: 8, cursor: 'pointer',
        border: '1px solid var(--bdr)', borderLeftWidth: 3, borderLeftColor: statusColor,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'relative' }}>
      {canDeleteBrief && (
        <button
          onClick={(e) => { e.stopPropagation(); onDeleteInline?.(b, e); }}
          aria-label="Delete brief"
          style={{
            position: 'absolute', top: 8, right: 8,
            width: 28, height: 28, borderRadius: 8,
            border: '1px solid var(--bdr)', background: 'rgba(0,0,0,.04)',
            color: 'rgba(0,0,0,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, cursor: 'pointer',
          }}
        >
          <Trash2 size={13} />
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingRight: canDeleteBrief ? 32 : 0 }}>
        <CompanyLogo name={b.company_name || b.lead_name} website={lead?.website} size={32} />
        <span style={{ fontSize: 13, color: 'var(--t2)', flex: 1, fontWeight: 400 }}>{b.company_name || b.lead_name || 'Unknown'}</span>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: statusColor + '18', color: statusColor, fontWeight: 500 }}>{b.status}</span>
      </div>
      <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--t1)', lineHeight: 1.3, marginBottom: 8 }}>
        {b.title || 'Untitled'}
        {b.is_retainer && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--cd)', color: 'var(--c)', marginLeft: 6, verticalAlign: 'middle' }}>RET</span>}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: 'var(--t3)' }}>
        {pc && <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: pc.overdue ? '#EF4444' : 'var(--t3)' }}><Clock size={10} />{pc.text}</span>}
        {b.assigned_to_name && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 18, height: 18, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, background: avatarColor.bg, color: avatarColor.color }}>{getInitials(b.assigned_to_name)}</div>
          {b.assigned_to_name.split(' ')[0]}
        </span>}
        {b.priority && b.priority !== 'Medium' && <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: PRIORITY_COLORS[b.priority] }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: PRIORITY_COLORS[b.priority] }} />{b.priority}
        </span>}
        {canViewFinancials && b.opportunity_value_lakhs > 0 && <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#22C55E', fontFamily: 'var(--font-mono)' }}>{fmtValue(b.opportunity_value_lakhs)}</span>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function BizDevBriefs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const canViewFinancials = useCanViewFinancials();
  const isMobile = useIsMobile();
  const [mainTab, setMainTab] = useState(() => localStorage.getItem("briefs_tab") || "my");
  const [myWork, setMyWork] = useState({ assigned: [], to_review: [], delegated: [] });
  const [mySubTab, setMySubTab] = useState("todo");
  const [briefs, setBriefs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // URL-backed filters/view — reload-safe, shareable links
  const [filter, setFilter] = useUrlState("filter", "All");
  const [view, setView] = useUrlState("view", isMobile ? "list" : "kanban");
  const [searchQuery, setSearchQuery] = useUrlState("q", "");
  const [showAdd, setShowAdd] = useState(!!searchParams.get("new") || !!searchParams.get("title") || location.pathname.endsWith("/new"));
  const [form, setForm] = useState({
    title: searchParams.get("title") || "", lead_id: searchParams.get("lead_id") || searchParams.get("lead") || "",
    deep_brief: "", status: "New", service_type: "", priority: "Medium",
    opportunity_value_lakhs: "", pitch_date: "", pitch_time: "",
    pitch_location: "", assigned_to: "",
    review_1_date: "", review_1_time: "", review_2_date: "", review_2_time: "",
    revenue_generated_lakhs: "", revenue_attribution_month: "", execution_month: "",
    sub_assigned_to: "", reviewer_1: "", reviewer_2: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Files staged before submit. On create success we POST each one to the
  // new brief_id's attachments endpoint. Failures are non-fatal — the brief
  // exists, user can retry uploads from the detail page.
  const [stagedFiles, setStagedFiles] = useState([]);
  const [attachDragOver, setAttachDragOver] = useState(false);
  const [sortBy, setSortBy] = useUrlState("sort", "pitch_date");
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [dateFilter, setDateFilter] = useUrlState("date", "all");
  const [retainerFilter, setRetainerFilter] = useUrlState("retainer", "all");
  const [showFilters, setShowFilters] = useState(false);
  const [leadFilter, setLeadFilter] = useState([]);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [dragBriefId, setDragBriefId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [quickAddCol, setQuickAddCol] = useState(null);
  const [quickAddTitle, setQuickAddTitle] = useState("");
  const searchRef = useRef(null);

  useEffect(() => { loadData(); loadMyBriefs(); }, []);
  useEffect(() => {
    if (!showAssigneeDropdown && !showLeadDropdown) return;
    const close = (e) => {
      if (showAssigneeDropdown && !e.target.closest('[data-assignee-dropdown]')) setShowAssigneeDropdown(false);
      if (showLeadDropdown && !e.target.closest('[data-lead-dropdown]')) setShowLeadDropdown(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showAssigneeDropdown, showLeadDropdown]);

  // Keyboard shortcuts via central registry (desktop only — useShortcuts auto-disables on touch)
  useShortcuts(
    [
      { keys: "/", description: "Focus search", group: "Briefs",
        handler: () => searchRef.current?.focus() },
      { keys: "n", description: "New brief", group: "Briefs",
        handler: () => setShowAdd(true) },
      { keys: "1", description: "List view", group: "Briefs",
        handler: () => setView("list") },
      { keys: "2", description: "Kanban view", group: "Briefs",
        handler: () => setView("kanban") },
      { keys: "escape", description: "Close add modal",
        handler: () => setShowAdd((v) => v ? false : v) },
    ],
    { enabled: !loading }
  );

  const loadMyBriefs = async () => {
    try {
      const res = await api.get("/briefs/my-work");
      setMyWork(res.data || { assigned: [], to_review: [], delegated: [] });
    } catch (e) { console.error("Failed to load my briefs:", e); }
  };

  const loadData = async () => {
    try {
      const [b, l, t] = await Promise.all([api.get("/bizdev/briefs"), api.get("/leads"), api.get("/team-members").catch(() => ({ data: [] }))]);
      setBriefs(b.data); setLeads(l.data); setTeamMembers(t.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDrop = async (briefId, newStatus) => {
    const prevBriefs = briefs;
    setBriefs(prev => prev.map(b => (b.brief_id || b._id || b.id) === briefId ? { ...b, status: newStatus } : b));
    try {
      await api.put(`/bizdev/briefs/${briefId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
    } catch (e) {
      // Surface the real backend reason — e.g. "Revenue Generated (₹ Lakhs) is
      // required to mark as Won/Executed". Without this, every failed drag
      // looked like a silent / random bug to the user.
      const detail = e?.response?.data?.detail || e?.message || "Failed to move brief";
      toast.error(detail);
      console.error("brief move failed", e?.response?.data || e);
      setBriefs(prevBriefs);
    }
  };

  const handleQuickAdd = async (status) => {
    if (!quickAddTitle.trim()) return;
    try {
      const res = await api.post("/bizdev/briefs", { title: quickAddTitle.trim(), status, lead_id: leads[0]?.lead_id || "", service_type: "Digital Campaign", priority: "Medium", deep_brief: "" });
      toast.success("Brief created");
      setQuickAddCol(null); setQuickAddTitle("");
      const briefId = res.data?.brief_id;
      if (briefId) navigate(`/bizdev/briefs/${briefId}`);
      else loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create brief"); }
  };


  // Helper: stage files picked from the file picker or drag-drop.
  // Enforces the same 25MB / mime whitelist the backend checks, so users
  // get instant feedback rather than waiting until after brief creation.
  const stageAttachmentFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const allowed = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".png", ".jpg", ".jpeg", ".webp", ".txt", ".md", ".csv"];
    const oversize = files.filter((f) => f.size > 25 * 1024 * 1024).map((f) => f.name);
    const badType = files.filter((f) => {
      const dot = f.name.lastIndexOf(".");
      const ext = dot >= 0 ? f.name.slice(dot).toLowerCase() : "";
      return !allowed.includes(ext);
    }).map((f) => f.name);
    if (oversize.length) toast.error(`${oversize.length} file${oversize.length === 1 ? "" : "s"} over 25MB — skipped`);
    if (badType.length) toast.error(`Unsupported: ${badType.join(", ").slice(0, 80)}`);
    const ok = files.filter((f) => f.size <= 25 * 1024 * 1024 && !badType.includes(f.name));
    if (ok.length) setStagedFiles((prev) => [...prev, ...ok]);
  };

  const reloadAll = () => { loadData(); loadMyBriefs(); };

  // Who's allowed to delete. Mirrors the backend can_delete() check —
  // Admin / SrManager / super-admin by email. Non-qualifying users never see
  // the trash icon in the first place; the server enforces independently.
  const canDeleteBrief = (() => {
    const role = (user?.role || "").toLowerCase();
    return role === "admin" || role === "srmanager" || isSuperAdmin(user?.email);
  })();

  // Inline delete with 5s undo — same pattern as Library + MyDashboard.
  // Click trash on a row → brief vanishes from the list optimistically;
  // toast offers Undo. Server DELETE only fires on auto-close. If the
  // server call fails (permission, race), the brief is restored with an
  // error toast and the user isn't left wondering why it "came back".
  const deleteBriefInline = (brief, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const bid = brief.brief_id;
    const briefsSnap = briefs;
    setBriefs((prev) => prev.filter((x) => x.brief_id !== bid));
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(10);
    let undone = false;
    toast(`Deleted: ${(brief.title || "brief").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; setBriefs(briefsSnap); },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${bid}`); }
          catch (err) {
            console.error("Inline brief delete failed", err);
            setBriefs(briefsSnap);
            toast.error(err.response?.data?.detail || "Couldn't delete — restored");
          }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${bid}`); }
          catch (err) {
            console.error("Inline brief delete failed", err);
            setBriefs(briefsSnap);
            toast.error(err.response?.data?.detail || "Couldn't delete — restored");
          }
        }, 50);
      },
    });
  };
  usePullToRefresh(reloadAll);

  // My briefs sub-tab data
  const currentMyBriefs = useMemo(() => {
    if (mySubTab === "review") return myWork.to_review;
    if (mySubTab === "delegated") return myWork.delegated;
    return myWork.assigned;
  }, [myWork, mySubTab]);

  const displayedMyBriefs = useMemo(() => {
    let result = [...currentMyBriefs];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => (b.title || "").toLowerCase().includes(q) || (b.company_name || "").toLowerCase().includes(q));
    }
    return result;
  }, [currentMyBriefs, searchQuery]);

  const myTotalCount = myWork.assigned.length + myWork.to_review.length + myWork.delegated.length;

  // Filtering
  const currentGroup = FILTER_GROUPS.find((g) => g.label === filter);
  let filtered = filter === "All" ? [...briefs] : briefs.filter((b) => currentGroup?.statuses.includes(b.status));
  if (searchQuery) filtered = filtered.filter(b => (b.title || "").toLowerCase().includes(searchQuery.toLowerCase()) || (b.company_name || "").toLowerCase().includes(searchQuery.toLowerCase()) || (b.lead_name || "").toLowerCase().includes(searchQuery.toLowerCase()));
  if (assigneeFilter.length > 0) filtered = filtered.filter(b => assigneeFilter.includes(b.assigned_to));
  if (leadFilter.length > 0) filtered = filtered.filter(b => leadFilter.includes(b.lead_id));
  if (retainerFilter === "retainer") filtered = filtered.filter(b => b.is_retainer === true);
  else if (retainerFilter === "non-retainer") filtered = filtered.filter(b => !b.is_retainer);
  if (dateFilter !== "all") {
    filtered = filtered.filter(b => {
      const d = b.pitch_date || b.due_date;
      if (!d) return false;
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const due = new Date(d + "T00:00:00"); due.setHours(0, 0, 0, 0);
      const diff = Math.round((due - now) / 86400000);
      if (dateFilter === "overdue") return diff < 0;
      if (dateFilter === "today") return diff === 0;
      if (dateFilter === "3days") return diff >= 0 && diff <= 3;
      if (dateFilter === "7days") return diff >= 0 && diff <= 7;
      return true;
    });
  }

  // Sort
  filtered.sort((a, b) => {
    if (sortBy === "pitch_date") return (a.pitch_date || "9999") < (b.pitch_date || "9999") ? -1 : 1;
    if (sortBy === "priority") { const p = { Critical: 0, High: 1, Medium: 2, Low: 3 }; return (p[a.priority] || 2) - (p[b.priority] || 2); }
    if (sortBy === "opportunity") return (b.opportunity_value_lakhs || 0) - (a.opportunity_value_lakhs || 0);
    return 0;
  });
  filtered.sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0));

  // Kanban grouping
  const grouped = {}; BRIEF_STATUSES.forEach(s => grouped[s.key] = []);
  filtered.forEach(b => { const k = b.status || "New"; if (grouped[k]) grouped[k].push(b); });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;

  // ═══════ ADD BRIEF FORM ═══════
  if (showAdd) {
    // ═══════ NEW BRIEF — 4-STEP CAROUSEL ═══════
    // Refactored from a 18-field wall to step-progressive Essentials →
    // Schedule → Team → Details. Step 1 has the "Save & finish later"
    // escape hatch; the team can ship a brief with title + lead +
    // service_type and hydrate the rest from the detail page.
    //
    // Preserves: duplicate-check (now via confirm() instead of inline
    // panel), staged attachment upload after creation, conditional
    // revenue fields when status=Won/Executed.
    const closeAdd = () => {
      setShowAdd(false);
      setStagedFiles([]);
      if (searchParams.get("new")) navigate("/bizdev/briefs");
    };

    const initialState = {
      title: searchParams.get("title") || "",
      lead_id: "",
      service_type: "",
      status: "New",
      priority: "Medium",
      deep_brief: "",
      pitch_date: "",
      pitch_time: "",
      pitch_location: "",
      review_1_date: "",
      review_1_time: "",
      review_2_date: "",
      review_2_time: "",
      assigned_to: "",
      sub_assigned_to: "",
      reviewer_1: "",
      reviewer_2: "",
      opportunity_value_lakhs: "",
      revenue_generated_lakhs: "",
      revenue_attribution_month: "",
      execution_month: "",
    };

    const TIME_OPTS = [
      "09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30",
      "14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30",
      "19:00","19:30","20:00",
    ];

    const buildPayload = (form, minimum = false) => {
      const fromJotId = searchParams.get("from_jot_id") || null;
      const p = {
        title: form.title?.trim(),
        lead_id: form.lead_id,
        service_type: form.service_type,
        status: minimum ? "New" : (form.status || "New"),
        priority: minimum ? "Medium" : form.priority,
        // Threaded from /me when promoting a jot → brief. Backend soft-deletes
        // that jot atomically with this brief create. (bug_cc946f51b211)
        from_jot_id: fromJotId,
      };
      if (minimum) return p;
      return {
        ...p,
        deep_brief: form.deep_brief || "",
        opportunity_value_lakhs: form.opportunity_value_lakhs ? parseFloat(form.opportunity_value_lakhs) : null,
        pitch_date: form.pitch_date || null,
        pitch_time: form.pitch_time || null,
        pitch_location: form.pitch_location || null,
        assigned_to: form.assigned_to || null,
        sub_assigned_to: form.sub_assigned_to || null,
        reviewer_1: form.reviewer_1 || null,
        reviewer_2: form.reviewer_2 || null,
        review_1_date: form.review_1_date || null,
        review_1_time: form.review_1_time || null,
        review_2_date: form.review_2_date || null,
        review_2_time: form.review_2_time || null,
        revenue_generated_lakhs: form.revenue_generated_lakhs ? parseFloat(form.revenue_generated_lakhs) : null,
        revenue_attribution_month: form.revenue_attribution_month || null,
        execution_month: form.execution_month || null,
      };
    };

    const uploadAttachmentsAfterCreate = async (briefId) => {
      if (!briefId || stagedFiles.length === 0) return { ok: 0, fail: 0 };
      let ok = 0, fail = 0;
      await Promise.all(stagedFiles.map(async (file) => {
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/bizdev/briefs/${briefId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch { fail += 1; }
      }));
      return { ok, fail };
    };

    const submit = async (form, minimum = false) => {
      // Dupe-check (full path only — Save & finish later skips for speed)
      if (!minimum) {
        try {
          const dup = await api.post("/bizdev/briefs/check-duplicate", { title: form.title, lead_id: form.lead_id });
          const dupes = dup.data?.duplicates || [];
          if (dupes.length > 0) {
            const lines = dupes.map(d => `• ${d.title} — ${d.company_name} (${Math.round(d.similarity * 100)}% match)`).join("\n");
            const ok = window.confirm(`Similar briefs found:\n\n${lines}\n\nCreate anyway?`);
            if (!ok) throw new Error("user-cancelled");
          }
        } catch (e) {
          if (e?.message === "user-cancelled") throw e;
          // dup-check API failure — proceed silently
        }
      }
      const res = await api.post("/bizdev/briefs", buildPayload(form, minimum));
      const briefId = res.data?.brief_id;
      const upload = !minimum ? await uploadAttachmentsAfterCreate(briefId) : { ok: 0, fail: 0 };
      const plural = (n) => n === 1 ? "" : "s";
      if (upload.fail > 0) {
        toast.success(`Brief created · ${upload.ok} file${plural(upload.ok)} attached, ${upload.fail} failed`);
      } else if (upload.ok > 0) {
        toast.success(`Brief created with ${upload.ok} attachment${plural(upload.ok)}`);
      } else {
        toast.success(minimum ? "Brief created — fill the rest later" : "Brief created");
      }
      setStagedFiles([]);
      setShowAdd(false);
      if (searchParams.get("new")) navigate("/bizdev/briefs");
      if (briefId) navigate(`/bizdev/briefs/${briefId}`);
      else loadData();
    };

    const inputCls = "w-full px-3 py-2.5 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-white text-[13px] placeholder:text-[#555] focus:outline-none focus:border-[#E31937]";
    const labelCls = "block text-[11px] font-semibold text-[var(--t2)] mb-1.5 tracking-wider uppercase";

    const stepEssentials = {
      key: "essentials",
      label: "Essentials",
      validate: (form) => {
        const errs = {};
        if (!form.title?.trim()) errs.title = "Title is required";
        if (!form.lead_id) errs.lead_id = "Pick a lead";
        if (!form.service_type) errs.service_type = "Pick a service type";
        return errs;
      },
      render: ({ form, setForm, errors }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            What's the brief, who's it for, and what kind of work. That's enough to ship — fill schedule, team, and details next.
          </p>
          <div>
            <label className={labelCls}>Brief Title *</label>
            <input
              autoFocus
              className={inputCls}
              value={form.title}
              onChange={(e) => setForm({ title: e.target.value })}
              placeholder="What's this brief about?"
            />
            {errors?.title && <p className="text-[11px] text-red-400 mt-1">{errors.title}</p>}
          </div>
          <div>
            <label className={labelCls}>Lead / Client *</label>
            <select
              className={inputCls}
              value={form.lead_id}
              onChange={(e) => setForm({ lead_id: e.target.value })}
            >
              <option value="">Select lead…</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.company_name}</option>)}
            </select>
            {errors?.lead_id && <p className="text-[11px] text-red-400 mt-1">{errors.lead_id}</p>}
          </div>
          <div>
            <label className={labelCls}>
              Service Type *
              <Hint id="brief.service_type">
                Picks the workflow + revenue rules. <b>Mainline Campaign</b> = mass-media; <b>Digital Campaign</b> = paid social/search; <b>Influencer Marketing</b> uses creator-margin math; <b>Retainer</b> reserves a monthly value.
              </Hint>
            </label>
            <select
              className={inputCls}
              value={form.service_type}
              onChange={(e) => setForm({ service_type: e.target.value })}
            >
              <option value="">Select service type…</option>
              {Object.keys(SERVICE_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors?.service_type && <p className="text-[11px] text-red-400 mt-1">{errors.service_type}</p>}
          </div>
        </div>
      ),
    };

    const stepSchedule = {
      key: "schedule",
      label: "Schedule",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            When you're pitching, plus two internal review checkpoints. All optional at minimum, but expected by the team workflow.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                Pitch Date
                <Hint id="brief.pitch_date">
                  When you're presenting to the client. Drives the alert engine — we'll nudge you if no deck exists 5 days before.
                </Hint>
              </label>
              <input type="date" className={inputCls} value={form.pitch_date}
                onChange={(e) => setForm({ pitch_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Pitch Time</label>
              <input type="time" className={inputCls} value={form.pitch_time}
                onChange={(e) => setForm({ pitch_time: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Pitch Location</label>
            <input className={inputCls} value={form.pitch_location}
              onChange={(e) => setForm({ pitch_location: e.target.value })}
              placeholder="e.g. Client office, Zoom, our studio…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Review 1 Date</label>
              <input type="date" className={inputCls} value={form.review_1_date}
                onChange={(e) => setForm({ review_1_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Review 1 Time</label>
              <select className={inputCls} value={form.review_1_time}
                onChange={(e) => setForm({ review_1_time: e.target.value })}>
                <option value="">Pick a time…</option>
                {TIME_OPTS.map((t) => {
                  const [h, m] = t.split(":");
                  const hr = parseInt(h);
                  return <option key={t} value={t}>{`${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`}</option>;
                })}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Review 2 Date</label>
              <input type="date" className={inputCls} value={form.review_2_date}
                onChange={(e) => setForm({ review_2_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Review 2 Time</label>
              <select className={inputCls} value={form.review_2_time}
                onChange={(e) => setForm({ review_2_time: e.target.value })}>
                <option value="">Pick a time…</option>
                {TIME_OPTS.map((t) => {
                  const [h, m] = t.split(":");
                  const hr = parseInt(h);
                  return <option key={t} value={t}>{`${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
      ),
    };

    const stepTeam = {
      key: "team",
      label: "Team",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-3">
          <p className="text-[12px] text-[#888]">
            Who's driving this brief. Reviewers approve before it can go Pitched.
          </p>
          <UserAutocomplete
            value={form.assigned_to}
            onChange={(v) => setForm({ assigned_to: v })}
            label="Assigned To"
            placeholder="Brief owner — runs the work"
          />
          <UserAutocomplete
            value={form.sub_assigned_to}
            onChange={(v) => setForm({ sub_assigned_to: v })}
            label="Sub Assigned To"
            placeholder="Secondary support"
          />
          <UserAutocomplete
            value={form.reviewer_1}
            onChange={(v) => setForm({ reviewer_1: v })}
            label="Reviewer 1"
            placeholder="First-pass reviewer"
          />
          <UserAutocomplete
            value={form.reviewer_2}
            onChange={(v) => setForm({ reviewer_2: v })}
            label="Reviewer 2"
            placeholder="Senior approver"
          />
        </div>
      ),
    };

    const stepDetails = {
      key: "details",
      label: "Details",
      optional: true,
      render: ({ form, setForm }) => (
        <div className="space-y-4">
          <p className="text-[12px] text-[#888]">
            Last polish — brief content, status, opportunity value, and any reference files.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status}
                onChange={(e) => setForm({ status: e.target.value })}>
                {["New","In Progress","Pitched","Won","Executed","Lost","On Hold"].map((s) =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <select className={inputCls} value={form.priority}
                onChange={(e) => setForm({ priority: e.target.value })}>
                {["Low","Medium","High","Critical"].map((p) =>
                  <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Opportunity (₹ Lakhs)</label>
            <input type="number" step="any" className={inputCls}
              value={form.opportunity_value_lakhs}
              onChange={(e) => setForm({ opportunity_value_lakhs: e.target.value })}
              placeholder="e.g. 25" />
          </div>

          {(form.status === "Won" || form.status === "Executed") && (
            <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/[.06] space-y-3">
              <p className="text-green-400 text-[11px] font-semibold tracking-wider uppercase">Revenue attribution (required for Won/Executed)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Revenue Generated (₹ Lakhs) *</label>
                  <input type="number" step="any" className={inputCls}
                    value={form.revenue_generated_lakhs}
                    onChange={(e) => setForm({ revenue_generated_lakhs: e.target.value })}
                    placeholder="Actual revenue" />
                </div>
                <div>
                  <label className={labelCls}>Execution Month *</label>
                  <input type="month" className={inputCls}
                    value={form.execution_month}
                    onChange={(e) => setForm({ execution_month: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Revenue Attribution Month *</label>
                <input type="month" className={inputCls}
                  value={form.revenue_attribution_month}
                  onChange={(e) => setForm({ revenue_attribution_month: e.target.value })} />
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>Brief Description</label>
            {ReactQuill ? (
              <ReactQuill theme="snow" value={form.deep_brief}
                onChange={(v) => setForm({ deep_brief: v })}
                placeholder="Describe the brief — context, goals, deliverables…" />
            ) : (
              <textarea className={inputCls} rows={4}
                value={form.deep_brief}
                onChange={(e) => setForm({ deep_brief: e.target.value })}
                placeholder="Describe the brief…"
                style={{ resize: "vertical" }} />
            )}
          </div>

          {/* Attachments — staged in parent state, uploaded after brief
              creation. 25MB / mime whitelist enforced client-side. */}
          <div>
            <label className={labelCls}>Attachments</label>
            {stagedFiles.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {stagedFiles.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--bdr)", background: "var(--s2)" }}>
                    <FileText size={14} style={{ color: "var(--t3)", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: "var(--t3)" }}>{(f.size / 1024 / 1024).toFixed(1)} MB</div>
                    </div>
                    <button type="button"
                      onClick={() => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      style={{ background: "transparent", border: "none", color: "var(--t3)", cursor: "pointer", padding: 4, display: "flex" }}
                      title="Remove"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <label
              onDragEnter={(e) => { e.preventDefault(); setAttachDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); setAttachDragOver(true); }}
              onDragLeave={() => setAttachDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                setAttachDragOver(false);
                stageAttachmentFiles(e.dataTransfer?.files);
              }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "14px 16px",
                border: `1.5px dashed ${attachDragOver ? "#60a5fa" : "var(--bdr)"}`,
                background: attachDragOver ? "rgba(96,165,250,.08)" : "var(--bg)",
                borderRadius: 10, cursor: "pointer",
                color: "var(--t2)", fontSize: 13, fontWeight: 500,
                transition: "all .15s",
              }}>
              <Paperclip size={14} style={{ color: "var(--t3)" }} />
              <span>{attachDragOver ? "Drop to attach" : stagedFiles.length === 0 ? "Drop files or click to pick" : "Add more"}</span>
              <input
                type="file" multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.md,.csv"
                onChange={(e) => { stageAttachmentFiles(e.target.files); e.target.value = ""; }}
                style={{ display: "none" }} />
            </label>
            <div style={{ fontSize: 11, color: "var(--t4, #3e3e3e)", marginTop: 6 }}>
              25 MB per file. Uploaded right after the brief is created.
            </div>
          </div>
        </div>
      ),
    };

    return (
      <FormCarousel
        title="New Brief"
        draftKey="brief"
        finalLabel="Create Brief"
        initialState={initialState}
        steps={[stepEssentials, stepSchedule, stepTeam, stepDetails]}
        onSaveMinimum={(form) => submit(form, true)}
        onSaveFull={(form) => submit(form, false)}
        onClose={closeAdd}
      />
    );
  }


  // ═══════ MOBILE VIEW — IG 4:5 BRIEF CARDS ═══════
  if (isMobile) {
    const getStatusTag = (status, pc) => {
      if (pc?.overdue) return { bg: '#FEE2E2', color: '#DC2626', text: 'Overdue' };
      if (status === 'Won') return { bg: '#ECFDF5', color: '#059669', text: 'Won' };
      if (status === 'Lost') return { bg: '#FEE2E2', color: '#DC2626', text: 'Lost' };
      if (pc) return { bg: '#ECFDF5', color: '#059669', text: pc.text };
      return { bg: '#DBEAFE', color: '#2563EB', text: status || 'Active' };
    };
    return (
      <div style={{ paddingBottom: 16 }}>
        {/* IG Filter Chips */}
        <div className="ig-scroll" style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, padding: '10px 16px', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {["All", "Active", "Pitched", "Won", "Overdue"].map(s => {
            const isActive = filter === s || (s === "All" && filter === "All");
            return (
              <button key={s} onClick={() => setFilter(s === "All" ? "All" : s)}
                style={{ padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: isActive ? '#262626' : '#EFEFEF', color: isActive ? '#FFF' : '#262626', border: 'none', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {s}
              </button>
            );
          })}
        </div>

        {/* Brief Cards — 4:5 IG style */}
        <div style={{ padding: '4px 0' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, margin: '0 16px', background: '#FFF', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
              <p style={{ color: '#8E8E8E', fontSize: 13 }}>No briefs match filters</p>
            </div>
          ) : filtered.map((b, i) => {
            const statusColor = STATUS_COLORS[b.status] || "#64748B";
            const pc = pitchCountdown(b.pitch_date);
            const lead = leads.find(l => l.lead_id === b.lead_id);
            const tag = getStatusTag(b.status, pc);
            const domain = (lead?.website || b.company_name || '').replace(/https?:\/\//, '').replace(/\/.*/, '').replace(/\s/g, '').toLowerCase();
            return (
              <div key={b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
                style={{ background: '#FFF', borderRadius: 16, margin: '0 16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,.06)', overflow: 'hidden', cursor: 'pointer', animation: `fu .3s ${i * 0.08}s backwards` }}>
                {/* Brand header row — IG post header style */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px' }}>
                  <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
                    style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'contain', background: '#EFEFEF', flexShrink: 0 }}
                    onError={(e) => e.target.style.display = 'none'} alt="" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{b.company_name || b.lead_name || 'Unknown'}</div>
                    <div style={{ fontSize: 12, color: '#8E8E8E' }}>{b.service_type || b.industry || ''}{b.is_retainer ? ' · Retainer' : ''}{lead?.industry ? ` · ${lead.industry}` : ''}</div>
                  </div>
                  <div style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: tag.bg, color: tag.color }}>{tag.text}</div>
                </div>
                {/* Brief content — large, spacious */}
                <div style={{ padding: '0 16px 14px' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#262626', lineHeight: 1.25, marginBottom: 8 }}>{b.title || 'Untitled'}</div>
                  {b.description && <div style={{ fontSize: 14, color: '#8E8E8E', lineHeight: 1.5, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{b.description}</div>}
                  {/* Assignee + created */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, fontSize: 12, color: '#8E8E8E' }}>
                    {b.assignee_name && <span>👤 <span style={{ fontWeight: 600, color: '#262626' }}>{b.assignee_name}</span></span>}
                    {b.created_at && <span>📅 {new Date(b.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
                    {b.pitch_date && <span>🎯 Pitch {new Date(b.pitch_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
                  </div>
                  {/* Stats grid — 4 columns, bigger */}
                  <div style={{ display: 'flex', border: '1px solid #EFEFEF', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
                    {[
                      { v: b.opportunity_value_lakhs ? `₹${b.opportunity_value_lakhs > 100 ? (b.opportunity_value_lakhs/100).toFixed(1)+'Cr' : b.opportunity_value_lakhs+'L'}` : '—', l: 'Value' },
                      { v: b.deliverables_count || b.sow_items?.length || '—', l: 'Assets' },
                      { v: `${Math.round(b.sow_delivered_pct || 0)}%`, l: 'Done' },
                      { v: pc ? pc.text : (b.status || '—'), l: pc ? (pc.overdue ? 'Overdue' : 'Pitch') : 'Status' },
                    ].map((s, j) => (
                      <div key={j} style={{ flex: 1, textAlign: 'center', padding: '14px 4px', borderRight: j < 3 ? '1px solid #EFEFEF' : 'none' }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: '#262626' }}>{s.v}</div>
                        <div style={{ fontSize: 9, color: '#8E8E8E', letterSpacing: '.04em', textTransform: 'uppercase', marginTop: 3 }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                  {/* Progress bar — thicker */}
                  <div style={{ height: 6, background: '#EFEFEF', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3, width: `${Math.min(100, (b.sow_delivered_pct || 0))}%`, background: statusColor, transition: 'width .3s' }} />
                  </div>
                </div>
                {/* Action buttons — bigger */}
                <div style={{ display: 'flex', gap: 8, padding: '0 16px 18px' }}>
                  <button style={{ flex: 1, padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#262626', color: '#FFF' }}>Open Brief</button>
                  <button onClick={(e) => e.stopPropagation()} style={{ flex: 1, padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#EFEFEF', color: '#262626' }}>Update</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ═══════ MAIN VIEW (DESKTOP) ═══════
  return (
    <div data-testid="bizdev-briefs" className="max-w-[1200px] mx-auto space-y-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-xl text-[var(--t1)] tracking-wide">BRIEFS</h1>
        <button data-testid="add-brief-btn" onClick={() => setShowAdd(true)}
          className="dm-desktop-only flex items-center gap-2 px-5 py-2.5 bg-[var(--r)] text-white text-[13px] font-bold rounded-full hover:bg-[var(--r)] transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/20 font-heading">
          <Plus size={16} /> New Brief
        </button>
      </div>

      {/* Mobile inline create */}
      <div className="dm-mobile-only" style={{ display: 'none', margin: '0 16px 12px', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1.5px dashed var(--bdr)', borderRadius: 12, cursor: 'pointer' }} onClick={() => setShowAdd(true)}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--rd)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--r)', fontSize: 16, fontWeight: 700 }}>+</div>
        <span style={{ fontSize: 13, color: 'var(--t3)' }}>New brief...</span>
      </div>

      {/* Tab Switcher: My Briefs / All Briefs */}
      <div className="flex gap-1 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] w-fit mb-4">
        <button onClick={() => { setMainTab("my"); localStorage.setItem("briefs_tab", "my"); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${mainTab === "my" ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
          <User size={14} /> My Briefs <span className="text-xs px-2 py-0.5 rounded-full bg-white/[.06] text-[#888]">{myTotalCount}</span>
        </button>
        <button onClick={() => { setMainTab("all"); localStorage.setItem("briefs_tab", "all"); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${mainTab === "all" ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
          <Users size={14} /> All Briefs <span className="text-xs px-2 py-0.5 rounded-full bg-white/[.06] text-[#888]">{briefs.length}</span>
        </button>
      </div>

      {/* ═══════ MY BRIEFS TAB ═══════ */}
      {mainTab === "my" && (
        <div className="space-y-3">
          {/* Search */}
          <div className="relative max-w-[320px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search my briefs..."
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-sm placeholder:text-[var(--t3)] focus:outline-none focus:border-[#A855F7] focus:shadow-[0_0_0_3px_rgba(168,85,247,.08)] transition-all" />
          </div>
          {/* Sub-tabs: To Do / To Review / Delegated */}
          <div className="flex gap-1 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] w-fit">
            {[
              { key: "todo", label: "Assigned to Me", count: myWork.assigned.filter(b => !["Won","Executed","Lost"].includes(b.status)).length, icon: "📋" },
              { key: "review", label: "To Review", count: myWork.to_review.filter(b => !["Won","Executed","Lost"].includes(b.status)).length, icon: "👁" },
              { key: "delegated", label: "Delegated", count: myWork.delegated.filter(b => !["Won","Executed","Lost"].includes(b.status)).length, icon: "📤" },
            ].map(s => (
              <button key={s.key} onClick={() => setMySubTab(s.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mySubTab === s.key ? "bg-[var(--s2)] text-white" : "text-[#666] hover:text-[var(--t1)]"}`}>
                <span>{s.icon}</span> {s.label}
                {s.count > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${mySubTab === s.key ? "bg-[var(--s3)] text-white" : "bg-[var(--s2)] text-[#888]"}`}>{s.count}</span>}
              </button>
            ))}
          </div>
          {/* Brief count */}
          <div className="text-xs text-[var(--t3)]">{displayedMyBriefs.length} brief{displayedMyBriefs.length !== 1 ? "s" : ""}</div>
          {/* Dashboard */}
          <MyBriefsDashboard briefs={displayedMyBriefs} navigate={navigate} onUpdate={reloadAll} mode={mySubTab} canViewFinancials={canViewFinancials} leads={leads} />
        </div>
      )}

      {/* ═══════ ALL BRIEFS TAB ═══════ */}
      {mainTab === "all" && (<>
      {/* Clean Filter Bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap dm-briefs-filter-bar">
        <div className="relative flex-1 max-w-[280px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
          <input ref={searchRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search briefs..."
            className="w-full pl-10 pr-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-white text-sm placeholder:text-[var(--t3)] focus:outline-none focus:border-[#A855F7] focus:shadow-[0_0_0_3px_rgba(168,85,247,.08)] transition-all" />
        </div>
        {FILTER_GROUPS.map((g) => (
          <button key={g.label} onClick={() => setFilter(filter === g.label && g.label !== "All" ? "All" : g.label)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${filter === g.label ? "bg-purple-500/10 border-purple-500/30 text-purple-400" : "border-[var(--bdr)] text-[#555] hover:text-[#999]"}`}>
            {g.label}
          </button>
        ))}
        <div className="w-px h-5 bg-[var(--s2)]" />
        {/* Lead/Brand filter */}
        <div className="relative dm-desktop-only" data-lead-dropdown>
          <button onClick={() => setShowLeadDropdown(!showLeadDropdown)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors flex items-center gap-1 ${leadFilter.length > 0 ? "bg-purple-500/10 border-purple-500/30 text-purple-400" : "border-[var(--bdr)] text-[#555] hover:text-[#999]"}`}>
            {leadFilter.length === 0 ? "All Brands" : `${leadFilter.length} Brand${leadFilter.length > 1 ? "s" : ""}`}
            <ChevronDown size={10} />
          </button>
          {showLeadDropdown && (
            <div className="absolute left-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[200px] max-h-[280px] overflow-y-auto">
              {leadFilter.length > 0 && <button onClick={() => setLeadFilter([])} className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-[var(--s2)]">Clear all</button>}
              {leads.map(l => (
                <label key={l.lead_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--s2)] cursor-pointer">
                  <input type="checkbox" checked={leadFilter.includes(l.lead_id)} onChange={() => setLeadFilter(prev => prev.includes(l.lead_id) ? prev.filter(id => id !== l.lead_id) : [...prev, l.lead_id])} className="rounded border-[var(--bdrh)] bg-transparent" />
                  <span className="text-xs text-[var(--t2)]">{l.company_name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {/* Assignee filter */}
        <div className="relative dm-desktop-only" data-assignee-dropdown>
          <button onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors flex items-center gap-1 ${assigneeFilter.length > 0 ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : "border-[var(--bdr)] text-[#555] hover:text-[#999]"}`}>
            {assigneeFilter.length === 0 ? "All Assignees" : `${assigneeFilter.length} Assignee${assigneeFilter.length > 1 ? "s" : ""}`}
            <ChevronDown size={10} />
          </button>
          {showAssigneeDropdown && (
            <div className="absolute left-0 top-full mt-1 z-50 bg-[var(--s1)] border border-[var(--bdrh)] rounded-lg shadow-xl py-1 min-w-[180px] max-h-[240px] overflow-y-auto">
              {assigneeFilter.length > 0 && <button onClick={() => setAssigneeFilter([])} className="w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-[var(--s2)]">Clear all</button>}
              {teamMembers.map(t => (
                <label key={t.user_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--s2)] cursor-pointer">
                  <input type="checkbox" checked={assigneeFilter.includes(t.user_id)} onChange={() => setAssigneeFilter(prev => prev.includes(t.user_id) ? prev.filter(id => id !== t.user_id) : [...prev, t.user_id])} className="rounded border-[var(--bdrh)] bg-transparent" />
                  <span className="text-xs text-[var(--t2)]">{t.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg text-xs text-[#888] outline-none cursor-pointer dm-desktop-only">
          <option value="pitch_date">Sort: Pitch Date</option>
          <option value="priority">Sort: Priority</option>
          <option value="opportunity">Sort: Revenue</option>
        </select>
        <div className="flex gap-1.5 p-1 bg-[var(--s1)] rounded-lg border border-[var(--bdr)] ml-auto dm-view-toggle">
          <button onClick={() => setView("list")} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${view === "list" ? "bg-[var(--s2)] text-white" : "text-[#555] hover:text-[var(--t1)]"}`}>
            <List size={14} /> List
          </button>
          <button onClick={() => setView("kanban")} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${view === "kanban" ? "bg-[var(--s2)] text-white" : "text-[#555] hover:text-[var(--t1)]"}`}>
            <LayoutGrid size={14} /> Board
          </button>
        </div>
      </div>

      {/* Collapsible Date + Retainer Filters */}
      <div className="mb-4">
        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-full transition-colors ${(dateFilter !== "all" || retainerFilter !== "all") ? "border-purple-500/30 bg-purple-500/10 text-purple-400" : "border-[var(--bdr)] text-[#555] hover:text-[var(--t1)]"}`}>
          <SlidersHorizontal size={12} /> Filters {showFilters ? '▾' : '▸'}
        </button>
        {showFilters && (
          <div className="flex flex-wrap gap-2 mt-2 p-3 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl">
            {[
              { key: "all", label: "All dates" }, { key: "overdue", label: "Overdue", color: "text-red-400" },
              { key: "today", label: "Due today", color: "text-amber-400" }, { key: "3days", label: "Next 3 days", color: "text-yellow-400" },
              { key: "7days", label: "Next 7 days", color: "text-green-400" },
            ].map(f => (
              <button key={f.key} onClick={() => setDateFilter(dateFilter === f.key && f.key !== "all" ? "all" : f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${dateFilter === f.key ? `bg-[var(--s2)] border border-[var(--bdrh)] ${f.color || "text-white"}` : "text-[#555] border border-[var(--bdr)] hover:text-[#999]"}`}>
                {f.label}
              </button>
            ))}
            <div className="w-px h-5 bg-[var(--s2)]" />
            {[{ key: "all", label: "All Accounts" }, { key: "retainer", label: "Retainer" }, { key: "non-retainer", label: "Non-Retainer" }].map(f => (
              <button key={f.key} onClick={() => setRetainerFilter(retainerFilter === f.key && f.key !== "all" ? "all" : f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${retainerFilter === f.key ? "bg-[var(--s2)] border border-cyan-500/30 text-cyan-400" : "text-[#555] border border-[var(--bdr)] hover:text-[#999]"}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Count + active filters */}
      <div className="text-xs text-[var(--t3)] mb-3">
        {filtered.length} brief{filtered.length !== 1 ? "s" : ""}
        {leadFilter.length > 0 && ` · ${leadFilter.length} brand${leadFilter.length > 1 ? "s" : ""}`}
        {assigneeFilter.length > 0 && ` · ${assigneeFilter.length} assignee${assigneeFilter.length > 1 ? "s" : ""}`}
      </div>

      {/* Hover-reveal for the kanban card trash. Kept scoped so it doesn't
          leak into the list view's separate group-hover handling. */}
      <style>{`
        .dm-brief-kanban-card:hover .dm-brief-kanban-trash,
        .dm-brief-kanban-card:focus-within .dm-brief-kanban-trash {
          opacity: 1;
        }
      `}</style>

      {/* ═══════ KANBAN VIEW ═══════ */}
      {view === "kanban" && (
        <div className="flex gap-2.5 overflow-x-auto pb-4 dm-briefs-kanban" style={{ minHeight: "60vh" }}>
          {BRIEF_STATUSES.filter(s => !KANBAN_STATUSES_HIDE.includes(s.key)).map(status => {
            const colBriefs = grouped[status.key] || [];
            const colTotal = colBriefs.reduce((s, b) => s + (b.opportunity_value_lakhs || 0), 0);
            const isDragOver = dragOverCol === status.key;
            return (
              <div key={status.key}
                onDragOver={e => { e.preventDefault(); setDragOverCol(status.key); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={e => { e.preventDefault(); setDragOverCol(null); if (dragBriefId) handleDrop(dragBriefId, status.key); setDragBriefId(null); }}
                style={{ flexShrink: 0, width: 260, borderRadius: 12, border: "1px solid var(--bdr)", display: "flex", flexDirection: "column", transition: "all .15s", background: isDragOver ? "var(--s2)" : "var(--s1)" }}>
                {/* Column header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderBottom: "1px solid var(--bdr)", flexShrink: 0 }}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: status.color }} />
                    <span className="text-xs font-semibold tracking-wider font-heading" style={{ color: status.color }}>{status.label.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: 11, color: "var(--t3)", background: "var(--s3)", padding: "2px 8px", borderRadius: 10 }}>{colBriefs.length}</span>
                    {canViewFinancials && colTotal > 0 && <span className="text-xs font-semibold font-heading" style={{ color: status.color }}>{fmtValue(colTotal)}</span>}
                  </div>
                </div>
                {/* Cards */}
                <div style={{ flex: 1, padding: 8, display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", maxHeight: "calc(100vh - 380px)" }}>
                  {colBriefs.length === 0 && (
                    <div style={{ textAlign: "center", padding: 32, fontSize: 11, color: "var(--t3)", border: "2px dashed var(--bdr)", borderRadius: 10 }}>
                      {isDragOver ? "Drop here" : status.key === "Won" ? "Win some briefs!" : "No briefs"}
                    </div>
                  )}
                  {colBriefs.map(b => {
                    const pc = pitchCountdown(b.pitch_date);
                    const lead = leads.find(l => l.lead_id === b.lead_id);
                    const domain = lead?.website && lead.website !== "Unknown" ? lead.website.replace(/^https?:\/\//, "").replace(/\/.*$/, "") : null;
                    const companyName = b.company_name || b.lead_name || "";
                    const initials = companyName ? companyName.trim().split(/\s+/).map(w => w[0]).join("").slice(0,2).toUpperCase() : "?";
                    return (
                      <div key={b.brief_id} draggable
                        className="dm-brief-kanban-card"
                        onDragStart={e => { e.dataTransfer.setData("briefId", b.brief_id); setDragBriefId(b.brief_id); }}
                        onDragEnd={() => setDragBriefId(null)}
                        onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
                        style={{ background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10, padding: "10px 12px", cursor: "pointer", transition: "all .15s", position: "relative" }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.15)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
                        {/* Hover-reveal trash — only shown to admin/srmanager. Small,
                            top-right, matches Library card pattern. stopPropagation to
                            keep the card's navigate() from firing. */}
                        {canDeleteBrief && (
                          <button
                            onClick={(e) => deleteBriefInline(b, e)}
                            title="Delete brief"
                            className="dm-brief-kanban-trash"
                            style={{
                              position: "absolute", top: 6, right: 6,
                              width: 22, height: 22, borderRadius: 6,
                              background: "rgba(0,0,0,.4)", border: "1px solid var(--bdr)",
                              color: "rgba(255,255,255,.6)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: 0, transition: "opacity .15s, background .15s, color .15s",
                              cursor: "pointer", padding: 0,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,.9)"; e.currentTarget.style.color = "white"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,.4)"; e.currentTarget.style.color = "rgba(255,255,255,.6)"; }}
                          >
                            <Trash2 size={11} />
                          </button>
                        )}
                        {/* Company row: logo + name */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          {domain ? (
                            <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`} width={20} height={20}
                              style={{ borderRadius: 4, objectFit: "contain", background: "var(--s3)", flexShrink: 0 }}
                              onError={e => { e.target.style.display = "none"; }} alt="" />
                          ) : (
                            <div style={{ width: 20, height: 20, borderRadius: 4, background: "var(--s3)", color: "var(--t3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
                          )}
                          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--t1)" }}>{companyName}</span>
                          {b.is_retainer && <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 3, background: "var(--cd)", color: "var(--c)", marginLeft: "auto", flexShrink: 0 }}>RET</span>}
                        </div>
                        {/* Brief title */}
                        <p style={{ fontSize: 12, color: "var(--t2)", lineHeight: 1.3, marginBottom: 4 }}>{b.title || "Untitled"}</p>
                        {/* Overdue only if overdue */}
                        {pc?.overdue && <p style={{ fontSize: 10, color: "#EF4444", display: "flex", alignItems: "center", gap: 3, marginTop: 4 }}><Clock size={9} />{pc.text}</p>}
                        {pc && !pc.overdue && pc.diff <= 3 && <p style={{ fontSize: 10, color: "var(--t3)", display: "flex", alignItems: "center", gap: 3, marginTop: 4 }}><Clock size={9} />{pc.text}</p>}
                      </div>
                    );
                  })}
                </div>
                {/* Quick add */}
                <div className="px-2 py-2 border-t border-[var(--bdr)]">
                  {quickAddCol === status.key ? (
                    <div className="flex gap-1.5">
                      <input value={quickAddTitle} onChange={e => setQuickAddTitle(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleQuickAdd(status.key); if (e.key === "Escape") { setQuickAddCol(null); setQuickAddTitle(""); } }}
                        placeholder="Brief title..." autoFocus
                        className="flex-1 px-2 py-1.5 rounded-md bg-[var(--s1)] border border-[var(--bdr)] text-white text-xs outline-none focus:border-[#A855F7]" />
                      <button onClick={() => handleQuickAdd(status.key)} className="px-2 py-1.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-heading">+</button>
                    </div>
                  ) : (
                    <button onClick={() => { setQuickAddCol(status.key); setQuickAddTitle(""); }}
                      className="w-full py-1.5 rounded-md border border-dashed border-[var(--bdr)] text-[#555] text-xs hover:border-[#555] hover:text-[#888] transition-colors">
                      + Add brief
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════ LIST VIEW ═══════ */}
      {view === "list" && (<>
        <div className="dm-mobile-only" style={{ display: 'none' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, background: 'var(--s1)', borderRadius: 16, border: '1px solid var(--bdr)' }}>
              <p style={{ color: 'var(--t3)', fontSize: 13 }}>No briefs match filters</p>
            </div>
          ) : filtered.map(b => <MobileBriefCard key={b.brief_id} b={b} navigate={navigate} leads={leads} canViewFinancials={canViewFinancials} canDeleteBrief={canDeleteBrief} onDeleteInline={deleteBriefInline} />)}
        </div>
        <div className="rounded-xl border border-[var(--bdr)] overflow-hidden dm-desktop-only">
          {filtered.length === 0 ? (
            <div className="text-center py-20 bg-[var(--s1)]">
              <FileText size={40} className="mx-auto text-[#222] mb-3" />
              <p className="text-[#555] text-sm font-medium">{filter !== "All" ? `No ${filter} briefs` : "No briefs yet"}</p>
              <p className="text-[#555] text-xs mt-1">Create a new brief to get started</p>
            </div>
          ) : (
            <table className="w-full border-collapse dm-briefs-table">
              <thead>
                <tr className="border-b border-[var(--bdr)]">
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading min-w-[250px]">Brief</th>
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading w-[120px]">Company</th>
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading w-[120px]">Assignee</th>
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading w-[80px]">Priority</th>
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading w-[110px]">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-left sticky top-0 bg-[var(--s1)] z-10 font-heading w-[140px]">Pitch Date</th>
                  {canViewFinancials && <th className="px-4 py-3 text-xs font-semibold text-[#888] tracking-wider uppercase text-right sticky top-0 bg-[var(--s1)] z-10 font-heading w-[80px]">Value</th>}
                  {canDeleteBrief && <th className="px-3 py-3 sticky top-0 bg-[var(--s1)] z-10 w-[40px]" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => {
                  const pc = pitchCountdown(b.pitch_date);
                  const svcColor = SERVICE_COLORS[b.service_type] || "#06B6D4";
                  const avatarColor = getAvatarColor(b.assigned_to_name);
                  return (
                    <tr key={b.brief_id}
                      onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
                      className={`border-b border-[var(--bdr)] cursor-pointer transition-colors group ${pc?.overdue ? "bg-red-500/[.015] hover:bg-red-500/[.03]" : "hover:bg-white/[.02]"}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={async (e) => { e.stopPropagation(); await api.post(`/bizdev/briefs/${b.brief_id}/star`); loadData(); }}
                            className={`text-sm flex-shrink-0 ${b.starred ? "text-yellow-400" : "text-[#222] hover:text-yellow-400"} transition-colors`}>
                            {b.starred ? "★" : "☆"}
                          </button>
                          <div>
                            <p className="text-sm font-medium text-white/90 leading-tight">{b.title}
                              {b.is_retainer && <span className="ml-1.5 text-[8px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">RET</span>}
                            </p>
                            <span className="text-xs" style={{ color: svcColor }}>{b.service_type || ""}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CompanyLogo name={b.company_name || b.lead_name || ""} website={leads.find(l => l.lead_id === b.lead_id)?.website} size={20} />
                          <span className="text-sm text-purple-400/80">{b.company_name || b.lead_name || ""}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {b.assigned_to_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold flex-shrink-0" style={{ background: avatarColor.bg, color: avatarColor.color }}>
                              {getInitials(b.assigned_to_name)}
                            </div>
                            <span className="text-xs text-[var(--t2)] truncate">{b.assigned_to_name}</span>
                          </div>
                        ) : <span className="text-xs text-[#555]">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs" style={{ color: PRIORITY_COLORS[b.priority] || "#666" }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRIORITY_COLORS[b.priority] || "#666" }} />
                          {b.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <StatusDropdown briefId={b.brief_id} currentStatus={b.status} onUpdate={reloadAll} />
                      </td>
                      <td className="px-4 py-3">
                        {b.pitch_date ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm text-[#888]">{formatDate(b.pitch_date)}</span>
                            {pc?.text && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${pc.bg}`} style={{ color: pc.color }}>{pc.text}</span>}
                          </div>
                        ) : <span className="text-xs text-[#555]">No date</span>}
                      </td>
                      {canViewFinancials && (
                        <td className="px-4 py-3 text-right">
                          {b.opportunity_value_lakhs ? <span className="text-sm font-semibold text-green-400 font-heading">{fmtValue(b.opportunity_value_lakhs)}</span> : <span className="text-[#555]">—</span>}
                        </td>
                      )}
                      {canDeleteBrief && (
                        <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => deleteBriefInline(b, e)}
                            title="Delete brief"
                            className="p-1.5 rounded-md text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </>)}

      </>)}

      {/* Keyboard shortcuts hint */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex gap-4 px-5 py-2 bg-[var(--s1)]/95 border border-[var(--bdr)] rounded-xl backdrop-blur-sm opacity-0 hover:opacity-100 transition-opacity">
        {[{ key: "/", label: "Search" }, { key: "N", label: "New brief" }, { key: "1", label: "List" }, { key: "2", label: "Board" }].map(s => (
          <div key={s.key} className="flex items-center gap-1.5 text-xs text-[#777]">
            <kbd className="px-1.5 py-0.5 bg-[var(--s2)] border border-[var(--bdrh)] rounded text-xs text-[#888] font-heading">{s.key}</kbd>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}
